import { Pet, PetTypeIds } from 'types/Pet';
import { Room } from 'types/Room';
import { DaycareAvailabilityRoom } from 'types/Availability';

export type RoomEligibilityReason =
    | 'pet_type_not_allowed'
    | 'breed_restricted'
    | 'breed_unknown'
    | 'personality_restricted';

export interface RoomEligibility {
    eligible: boolean;
    reason?: RoomEligibilityReason;
    message?: string;
}

interface RoomRestrictions {
    petTypeList: Array<string>;
    restrictedDogBreedList: Array<number>;
    restrictedCatBreedList: Array<number>;
    restrictedPetTagList: Array<string>;
}

const normalizeRestrictions = (room: Room | DaycareAvailabilityRoom): RoomRestrictions => {
    // Handle both Room (snake_case) and DaycareAvailabilityRoom (camelCase)
    return {
        petTypeList:
            (room as Room).pet_type_list ?? (room as DaycareAvailabilityRoom).petTypeList ?? [],
        restrictedDogBreedList:
            (room as Room).restrictedDogBreedList ??
            (room as DaycareAvailabilityRoom).restrictedDogBreedList ??
            [],
        restrictedCatBreedList:
            (room as Room).restrictedCatBreedList ??
            (room as DaycareAvailabilityRoom).restrictedCatBreedList ??
            [],
        restrictedPetTagList:
            (room as Room).restrictedPetTagList ??
            (room as DaycareAvailabilityRoom).restrictedPetTagList ??
            []
    };
};

const isPetTypeAllowed = (restrictions: RoomRestrictions, pet: Pet): boolean => {
    return restrictions.petTypeList.includes(pet.type.id);
};

const checkBreedRestriction = (restrictions: RoomRestrictions, pet: Pet): RoomEligibility => {
    const hasDogBreedRestrictions = restrictions.restrictedDogBreedList.length > 0;
    const hasCatBreedRestrictions = restrictions.restrictedCatBreedList.length > 0;

    if (pet.type.id === PetTypeIds.DOG) {
        if (!pet.breed?.id) {
            // Pet has no breed data - if room has breed restrictions, we can't verify eligibility
            if (hasDogBreedRestrictions) {
                return {
                    eligible: false,
                    reason: 'breed_unknown',
                    message: 'Pet breed is required for this service'
                };
            }
            return { eligible: true };
        }

        if (restrictions.restrictedDogBreedList.includes(pet.breed.id)) {
            return {
                eligible: false,
                reason: 'breed_restricted',
                message: 'This breed is not allowed in this room'
            };
        }
    }

    if (pet.type.id === PetTypeIds.CAT) {
        if (!pet.breed?.id) {
            if (hasCatBreedRestrictions) {
                return {
                    eligible: false,
                    reason: 'breed_unknown',
                    message: 'Pet breed is required for this service'
                };
            }
            return { eligible: true };
        }

        if (restrictions.restrictedCatBreedList.includes(pet.breed.id)) {
            return {
                eligible: false,
                reason: 'breed_restricted',
                message: 'This breed is not allowed in this room'
            };
        }
    }

    return { eligible: true };
};

const checkPersonalityRestriction = (restrictions: RoomRestrictions, pet: Pet): RoomEligibility => {
    if (restrictions.restrictedPetTagList.length === 0) {
        return { eligible: true };
    }

    const allPersonalities = [
        ...(pet.negativePersonalityList ?? []),
        ...(pet.positivePersonalityList ?? [])
    ];

    for (const personality of allPersonalities) {
        if (restrictions.restrictedPetTagList.includes(personality)) {
            return {
                eligible: false,
                reason: 'personality_restricted',
                message: `Pets with "${personality}" personality are not allowed in this room`
            };
        }
    }

    return { eligible: true };
};

export const isPetAllowedInRoom = (room: Room | DaycareAvailabilityRoom, pet: Pet): boolean => {
    const eligibility = getPetRoomEligibility(room, pet);
    return eligibility.eligible;
};

export const getPetRoomEligibility = (
    room: Room | DaycareAvailabilityRoom,
    pet: Pet
): RoomEligibility => {
    const restrictions = normalizeRestrictions(room);

    // Check pet type first
    if (!isPetTypeAllowed(restrictions, pet)) {
        return {
            eligible: false,
            reason: 'pet_type_not_allowed',
            message: 'This pet type is not allowed in this room'
        };
    }

    // Check breed restrictions
    const breedCheck = checkBreedRestriction(restrictions, pet);
    if (!breedCheck.eligible) {
        return breedCheck;
    }

    // Check personality/tag restrictions
    const personalityCheck = checkPersonalityRestriction(restrictions, pet);
    if (!personalityCheck.eligible) {
        return personalityCheck;
    }

    return { eligible: true };
};

export const areAllPetsAllowedInRoom = (
    room: Room | DaycareAvailabilityRoom,
    pets: Array<Pet>
): boolean => {
    return pets.every(pet => isPetAllowedInRoom(room, pet));
};

export const getIneligiblePets = (
    room: Room | DaycareAvailabilityRoom,
    pets: Array<Pet>
): Array<{ pet: Pet; eligibility: RoomEligibility }> => {
    return pets
        .map(pet => ({
            pet,
            eligibility: getPetRoomEligibility(room, pet)
        }))
        .filter(result => !result.eligibility.eligible);
};
